<template>
  <nav
    ref="navEl"
    class="navbar navbar-expand-lg fixed-top glass-navbar"
    :class="{ 'scrolled': isScrolled, 'navbar-hidden': !isVisible }"
    @pointermove="trackPointer"
  >
    <!-- Décor : lueurs de la teinte de marque, halo du curseur, liseré -->
    <div class="nav-bg" aria-hidden="true">
      <span class="aurora aurora-a"></span>
      <span class="aurora aurora-b"></span>
      <span class="nav-spotlight"></span>
      <span class="nav-glowline"></span>
    </div>

    <div class="container-fluid px-4">
      <div class="navbar-wrapper d-flex align-items-center justify-content-between w-100">
        <router-link to="/" class="navbar-brand d-flex align-items-center" @click="closeMobileMenu">
          <div class="brand-icon">
            <i class="bi bi-building-fill"></i>
          </div>
          <div class="brand-text-container">
            <span class="brand-text">{{ $branding.appName }}</span>
            <span class="brand-subtitle">ERP BTP</span>
          </div>
        </router-link>

        <!-- Navigation principale (desktop) -->
        <ul v-if="isAuthenticated" class="navbar-nav main-nav d-none d-lg-flex align-items-center" @mouseleave="hidePill">
          <li
            class="nav-hover-pill"
            :class="{ 'is-visible': pill.visible }"
            :style="{ width: `${pill.width}px`, transform: `translateX(${pill.left}px)` }"
            role="presentation"
            aria-hidden="true"
          ></li>

          <li v-if="showDashboard" class="nav-item" @mouseenter="movePill">
            <router-link to="/" class="nav-link">
              <i class="bi bi-grid-1x2-fill nav-ico"></i>
              <span>Dashboard</span>
            </router-link>
          </li>

          <template v-for="section in navSections" :key="section.key">
            <li
              v-if="section.access"
              class="nav-item custom-dropdown"
              :class="{ 'is-dismissed': dismissedSection === section.key }"
              @mouseenter="movePill"
              @mouseleave="dismissedSection = null"
            >
              <button type="button" class="nav-link" :class="{ 'is-active': isSectionActive(section) }" aria-haspopup="true">
                <i :class="['bi', section.icon, 'nav-ico']"></i>
                <span>{{ section.label }}</span>
                <i class="bi bi-chevron-down nav-caret"></i>
              </button>
              <div class="nav-panel" :class="{ 'two-cols': section.items.length > 3 }">
                <div class="panel-label">{{ section.label }}</div>
                <div class="panel-grid">
                  <router-link
                    v-for="(item, index) in section.items"
                    :key="item.to"
                    :to="item.to"
                    class="panel-item"
                    :class="`tone-${item.tone}`"
                    :style="{ '--i': index }"
                    @click="dismissedSection = section.key"
                  >
                    <span class="icon-tile">
                      <i :class="['bi', item.icon]"></i>
                    </span>
                    <span class="item-text">
                      <span class="item-title">
                        {{ item.title }}
                        <i class="bi bi-arrow-right-short item-arrow"></i>
                      </span>
                      <span class="item-desc">{{ item.desc }}</span>
                    </span>
                  </router-link>
                </div>
              </div>
            </li>
          </template>

          <li v-for="link in extraLinks" :key="link.to" class="nav-item" @mouseenter="movePill">
            <router-link :to="link.to" class="nav-link">
              <i :class="['bi', link.icon, 'nav-ico']"></i>
              <span>{{ link.label }}</span>
            </router-link>
          </li>
        </ul>

        <ul class="navbar-nav nav-actions align-items-center flex-row">
          <li v-if="isAuthenticated" class="nav-item">
            <a :href="helpHref" target="_blank" rel="noopener" class="icon-btn help-link" title="Aide sur cet écran" aria-label="Ouvrir l'aide sur cet écran (nouvel onglet)">
              <i class="bi bi-question-circle"></i>
              <span class="d-none d-xxl-inline">Aide</span>
            </a>
          </li>
          <li v-if="isAuthenticated" class="nav-item">
            <NotificationCenter />
          </li>
          <li v-if="isAuthenticated" class="nav-divider d-none d-md-block" aria-hidden="true"></li>

          <li v-if="isAuthenticated" class="nav-item dropdown">
            <button type="button" class="user-profile-link" id="userDropdown" data-bs-toggle="dropdown" aria-expanded="false">
              <span class="avatar-circle">{{ userInitials }}</span>
              <span class="user-meta d-none d-xl-flex flex-column text-start">
                <span class="user-name">{{ user.name }}</span>
                <span class="user-role">{{ userRole }}</span>
              </span>
              <i class="bi bi-chevron-down user-caret d-none d-md-block"></i>
            </button>
            <ul class="dropdown-menu dropdown-menu-end user-dropdown" aria-labelledby="userDropdown">
              <li class="user-card">
                <span class="avatar-circle lg">{{ userInitials }}</span>
                <div class="user-card-text">
                  <div class="user-card-name">{{ user.name }}</div>
                  <div class="user-card-email">{{ user.email }}</div>
                </div>
              </li>
              <li>
                <button class="dropdown-item tone-indigo" @click="goToProfile">
                  <span class="icon-tile xs"><i class="bi bi-person-gear"></i></span>
                  <span>Mon Profil</span>
                </button>
              </li>
              <li>
                <button class="dropdown-item tone-indigo" @click="goToSettings">
                  <span class="icon-tile xs"><i class="bi bi-sliders2"></i></span>
                  <span>Paramètres</span>
                </button>
              </li>
              <li><hr class="dropdown-divider"></li>
              <li>
                <button class="dropdown-item tone-rose is-danger" @click="logout">
                  <span class="icon-tile xs"><i class="bi bi-box-arrow-right"></i></span>
                  <span>Déconnexion</span>
                </button>
              </li>
            </ul>
          </li>
          <li v-else class="nav-item">
            <router-link to="/login" class="btn btn-light btn-sm px-3 px-md-4 rounded-pill shadow-lg hover-lift">
              <span class="fw-semibold">Connexion</span>
            </router-link>
          </li>

          <!-- Hamburger (Mobile Only) -->
          <li class="nav-item d-lg-none">
            <button class="icon-btn mobile-toggle-btn" type="button" aria-label="Ouvrir le menu" @click="toggleMobileMenu">
              <div class="hamburger-icon" :class="{ 'active': isMobileMenuOpen }">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </button>
          </li>
        </ul>
      </div>
    </div>
  </nav>

  <Teleport to="body">
    <div class="mobile-overlay" :class="{ 'active': isMobileMenuOpen }" @click="closeMobileMenu"></div>

    <div class="mobile-drawer" :class="{ 'open': isMobileMenuOpen }">
      <div class="drawer-header">
        <div class="d-flex align-items-center gap-2">
          <div class="brand-icon sm">
            <i class="bi bi-building-fill"></i>
          </div>
          <span class="brand-text">{{ $branding.appName }}</span>
        </div>
        <button class="btn-close-drawer" aria-label="Fermer le menu" @click="closeMobileMenu">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <div class="drawer-body">
        <div v-if="isAuthenticated" class="user-card user-drawer-info">
          <span class="avatar-circle lg">{{ userInitials }}</span>
          <div class="user-card-text">
            <div class="user-card-name">{{ user.name }}</div>
            <div class="user-card-email">{{ userRole }}</div>
          </div>
        </div>

        <nav v-if="isAuthenticated" class="drawer-nav">
          <router-link v-if="showDashboard" to="/" class="mobile-nav-link tone-indigo" @click="closeMobileMenu">
            <span class="icon-tile sm"><i class="bi bi-grid-1x2-fill"></i></span>
            <span>Tableau de bord</span>
          </router-link>

          <template v-for="section in navSections" :key="section.key">
            <div class="nav-section-label">{{ section.label }}</div>
            <router-link
              v-for="item in section.items"
              :key="item.to"
              :to="item.to"
              class="mobile-nav-link"
              :class="`tone-${item.tone}`"
              @click="closeMobileMenu"
            >
              <span class="icon-tile sm"><i :class="['bi', item.icon]"></i></span>
              <span>{{ item.title }}</span>
            </router-link>
          </template>

          <template v-for="link in extraLinks" :key="link.to">
            <div class="nav-section-label">{{ link.mobileLabel }}</div>
            <router-link :to="link.to" class="mobile-nav-link" :class="`tone-${link.tone}`" @click="closeMobileMenu">
              <span class="icon-tile sm"><i :class="['bi', link.icon]"></i></span>
              <span>{{ link.label }}</span>
            </router-link>
          </template>

          <div class="nav-section-label">Assistance</div>
          <a :href="helpHref" target="_blank" rel="noopener" class="mobile-nav-link tone-indigo" @click="closeMobileMenu">
            <span class="icon-tile sm"><i class="bi bi-question-circle"></i></span>
            <span>Aide sur cet écran</span>
          </a>
        </nav>
      </div>

      <div class="drawer-footer">
        <button v-if="isAuthenticated" class="drawer-logout" @click="logout">
          <i class="bi bi-box-arrow-right"></i>
          <span>Déconnexion</span>
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute, isNavigationFailure } from 'vue-router'
import { helpHrefFor } from '../utils/helpLinks'
import { useAuth } from '../composables/useAuth'
import NotificationCenter from './NotificationCenter.vue'

export default {
  name: 'Navbar',
  components: {
    NotificationCenter
  },
  setup() {
    const router = useRouter()
    const currentRoute = useRoute()
    const {
      isAuthenticated,
      user,
      logout: authLogout,
      isAdmin,
      isDirector,
      isCostController,
      isHR,
      isPointeur,
      isSupervisor,
      isQhsePays,
      isQhseChantier,
      isRelaisQhse,
      isQhseUser,
      hasPermission
    } = useAuth()

    const isScrolled = ref(false)
    const isVisible = ref(true)
    const lastScrollY = ref(0)
    const scrollThreshold = 100 // Minimal scroll before hiding
    const isMobileMenuOpen = ref(false)
    // Section whose hover panel was just used: kept closed until the pointer leaves it
    const dismissedSection = ref(null)

    const navEl = ref(null)
    // Glass pill that glides under the hovered top-level entry
    const pill = reactive({ left: 0, width: 0, visible: false })
    let pillHovered = false

    const movePill = (event) => {
      const entry = event.currentTarget
      pillHovered = true
      pill.left = entry.offsetLeft
      pill.width = entry.offsetWidth
      // First hover: place the pill before showing it, so it fades in instead of sliding from the edge
      if (!pill.visible) requestAnimationFrame(() => { pill.visible = pillHovered })
    }

    const hidePill = () => {
      pillHovered = false
      pill.visible = false
    }

    // Feeds the spotlight that follows the pointer across the bar
    const trackPointer = (event) => {
      if (!navEl.value) return
      const rect = navEl.value.getBoundingClientRect()
      navEl.value.style.setProperty('--mx', `${event.clientX - rect.left}px`)
      navEl.value.style.setProperty('--my', `${event.clientY - rect.top}px`)
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY

      // Update scrolled state (floating effect)
      isScrolled.value = currentScrollY > 20

      // Navigation visibility logic
      if (currentScrollY < scrollThreshold) {
        isVisible.value = true
      } else if (currentScrollY > lastScrollY.value) {
        // Scrolling down
        isVisible.value = false
      } else {
        // Scrolling up
        isVisible.value = true
      }

      lastScrollY.value = currentScrollY
    }

    onMounted(() => {
      window.addEventListener('scroll', handleScroll)
    })

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll)
    })

    const toggleMobileMenu = () => {
      isMobileMenuOpen.value = !isMobileMenuOpen.value
    }

    const closeMobileMenu = () => {
      isMobileMenuOpen.value = false
    }

    const hasProjetAccess = computed(() => {
      return isAdmin.value || isQhsePays.value || (!isHR.value && !isPointeur.value && !isSupervisor.value && !isQhseChantier.value && !isRelaisQhse.value) || hasPermission('projects.view')
    })

    const hasRessourcesAccess = computed(() => {
      return canViewResources.value || canViewPersonnel.value || (!isHR.value && !isPointeur.value && !isSupervisor.value)
    })

    const hasChantierAccess = computed(() => {
      return (
        isAdmin.value ||
        isPointeur.value ||
        isSupervisor.value ||
        canViewSiteReports.value ||
        canViewAllSiteReports.value ||
        canValidateSiteReports.value ||
        canViewExpenseReports.value ||
        canViewPlanningTracking.value
      )
    })

    const canViewAllSiteReports = computed(() => {
      return (
        isAdmin.value ||
        isDirector.value ||
        isCostController.value ||
        isSupervisor.value ||
        hasPermission('site_reports.view_all')
      )
    })

    const canViewExpenseReports = computed(() => {
      return hasPermission('daily_reports.view')
    })

    const canViewPlanningTracking = computed(() => {
      return hasPermission('planning_tracking.view')
    })

    const canViewResources = computed(() => {
      return hasPermission('resources.view')
    })

    const canViewPersonnel = computed(() => {
      return hasPermission('personnel.view')
    })

    const canViewSiteReports = computed(() => {
      return hasPermission('site_reports.view')
    })

    const canValidateSiteReports = computed(() => {
      return hasPermission('site_reports.validate')
    })

    const canCreateSiteReports = computed(() => {
      return hasPermission('site_reports.create')
    })

    const canViewBudgetControl = computed(() => {
      return isAdmin.value || hasPermission('budget_control.view')
    })

    const showDashboard = computed(() => !isPointeur.value && !isSupervisor.value)

    // Single source for the desktop panels and the mobile drawer. `access` gates the
    // desktop entry only; the drawer lists every section that has visible items.
    const navSections = computed(() => {
      const isFieldRole = isPointeur.value || isSupervisor.value
      const isOffice = !isHR.value && !isFieldRole

      return [
        {
          key: 'projet',
          label: 'Gestion Projet',
          icon: 'bi-kanban-fill',
          access: hasProjetAccess.value,
          items: [
            { to: '/projects', title: 'Projets', desc: 'Liste et suivi des chantiers', icon: 'bi-buildings', tone: 'indigo', show: isOffice },
            { to: '/planning', title: 'Planning', desc: 'Calendrier et échéances', icon: 'bi-calendar2-week', tone: 'sky', show: isOffice },
            { to: '/budget', title: 'Récap Budgétaire', desc: 'Gestion financière globale', icon: 'bi-cash-coin', tone: 'emerald', show: isAdmin.value },
            { to: '/budget-control', title: 'Contrôle Budgétaire', desc: 'Analyse des coûts', icon: 'bi-speedometer2', tone: 'amber', show: canViewBudgetControl.value },
            { to: '/activity-liquidity', title: 'Activité & Liquidité', desc: 'Marge à date & Trésorerie chantiers', icon: 'bi-graph-up-arrow', tone: 'violet', show: isAdmin.value },
            { to: '/qhse', title: 'QHSE', desc: 'Qualité, Hygiène, Sécurité, Environnement', icon: 'bi-shield-check', tone: 'teal', show: isQhseUser.value || isAdmin.value }
          ]
        },
        {
          key: 'ressources',
          label: 'Ressources',
          icon: 'bi-people-fill',
          access: hasRessourcesAccess.value,
          items: [
            { to: '/resources', title: 'Ressources', desc: 'Annuaire des ressources', icon: 'bi-boxes', tone: 'indigo', show: canViewResources.value && !isFieldRole },
            { to: '/personnel', title: 'Personnel', desc: 'Gestion des employés', icon: 'bi-person-vcard', tone: 'sky', show: canViewPersonnel.value && !isFieldRole },
            { to: '/materiel', title: 'Matériel', desc: 'Parc engins et équipements', icon: 'bi-truck-front', tone: 'orange', show: isOffice }
          ]
        },
        {
          key: 'chantier',
          label: 'Chantier',
          icon: 'bi-cone-striped',
          access: hasChantierAccess.value,
          items: [
            { to: '/daily-expense-reports', title: 'Dépenses', desc: 'Rapports de frais journaliers', icon: 'bi-receipt', tone: 'rose', show: canViewExpenseReports.value },
            { to: '/site-reports/overview', title: 'Tous les Rapports', desc: 'Consultation & filtres avancés', icon: 'bi-journals', tone: 'indigo', show: canViewAllSiteReports.value },
            { to: '/site-reports', title: 'Rapport Chantier', desc: 'Saisie des rapports quotidiens', icon: 'bi-clipboard2-data', tone: 'sky', show: canViewSiteReports.value },
            { to: '/photos-chantier', title: 'Photos Chantier', desc: 'Ajouter des photos par activité', icon: 'bi-camera', tone: 'violet', show: canCreateSiteReports.value },
            { to: '/site-reports/validation', title: 'Validation', desc: 'Contrôle et approbation', icon: 'bi-patch-check', tone: 'emerald', show: canValidateSiteReports.value },
            { to: '/suivi-planning', title: 'Suivi Planning', desc: 'Avancement et écarts sur la baseline', icon: 'bi-bar-chart-steps', tone: 'amber', show: canViewPlanningTracking.value }
          ]
        }
      ]
        .map(section => ({ ...section, items: section.items.filter(item => item.show) }))
        .filter(section => section.items.length > 0)
    })

    const extraLinks = computed(() => {
      return [
        { to: '/team-management', label: 'Équipe', mobileLabel: 'Équipe', icon: 'bi-diagram-3-fill', tone: 'indigo', show: isDirector.value },
        { to: '/admin', label: 'Admin', mobileLabel: 'Administration', icon: 'bi-shield-lock-fill', tone: 'violet', show: isAdmin.value }
      ].filter(link => link.show)
    })

    // A section is highlighted when the current screen belongs to one of its entries
    const isSectionActive = (section) => {
      const path = currentRoute.path
      return section.items.some(item => path === item.to || path.startsWith(`${item.to}/`))
    }

    const userInitials = computed(() => {
      if (!user.value || !user.value.name) return 'U'
      return user.value.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2)
    })

    const userRole = computed(() => {
      if (isAdmin.value) return 'Administrateur'
      if (isDirector.value) return 'Directeur'
      if (isHR.value) return 'RH'
      if (isQhsePays.value) return 'Resp. QHSE Pays'
      if (isQhseChantier.value) return 'Resp. QHSE Chantier'
      if (isRelaisQhse.value) return 'Relais QHSE'
      return 'Utilisateur'
    })

    const goToProfile = () => {
      closeMobileMenu()
      router.push('/profile')
    }

    const goToSettings = () => {
      closeMobileMenu()
      router.push('/settings')
    }

    const logout = async () => {
      closeMobileMenu()
      // Leave the current page first: its unsaved-changes guard asks with the app dialog and
      // can cancel. The session is closed only once the page has actually been left.
      const failure = await router.push({ path: '/login', query: { logout: '1' } })
      if (isNavigationFailure(failure)) return
      await authLogout()
    }

    // Aide contextuelle : page de l'aide correspondant à l'écran affiché
    const helpHref = computed(() => helpHrefFor(currentRoute))

    return {
      helpHref,
      isAuthenticated,
      user,
      showDashboard,
      navSections,
      extraLinks,
      isSectionActive,
      dismissedSection,
      navEl,
      pill,
      movePill,
      hidePill,
      trackPointer,
      userInitials,
      userRole,
      isScrolled,
      isVisible,
      isMobileMenuOpen,
      toggleMobileMenu,
      closeMobileMenu,
      goToProfile,
      goToSettings,
      logout
    }
  }
}
</script>

<style scoped>
/*
  ------------------------------------------------------------------
  COLOUR TONES (icon tiles)
  --tone: rgb triplet for tints / glows, --tone-ink: icon and text on
  light, --tone-from / --tone-to: gradient of the filled tile
  ------------------------------------------------------------------
*/
.tone-indigo  { --tone: 79, 70, 229;   --tone-ink: #4338ca; --tone-from: #6366f1; --tone-to: #4338ca; }
.tone-sky     { --tone: 14, 165, 233;  --tone-ink: #0369a1; --tone-from: #38bdf8; --tone-to: #0284c7; }
.tone-emerald { --tone: 16, 185, 129;  --tone-ink: #047857; --tone-from: #34d399; --tone-to: #059669; }
.tone-amber   { --tone: 245, 158, 11;  --tone-ink: #b45309; --tone-from: #fbbf24; --tone-to: #d97706; }
.tone-orange  { --tone: 249, 115, 22;  --tone-ink: #c2410c; --tone-from: #fb923c; --tone-to: #ea580c; }
.tone-rose    { --tone: 244, 63, 94;   --tone-ink: #be123c; --tone-from: #fb7185; --tone-to: #e11d48; }
.tone-violet  { --tone: 139, 92, 246;  --tone-ink: #6d28d9; --tone-from: #a78bfa; --tone-to: #7c3aed; }
.tone-teal    { --tone: 20, 184, 166;  --tone-ink: #0f766e; --tone-from: #2dd4bf; --tone-to: #0d9488; }

/*
  ------------------------------------------------------------------
  NAVBAR CONTAINER - "FLOATING ISLAND" CONCEPT
  ------------------------------------------------------------------
*/
.glass-navbar {
  background: linear-gradient(100deg, #0f1035 0%, #1b1a55 50%, #2e2a7a 100%) !important;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border-bottom: 0;
  box-shadow: 0 10px 28px -14px rgba(15, 16, 53, 0.55);
  padding: 0.75rem 0;
  transition: padding 0.4s ease, border-radius 0.4s ease, width 0.4s ease, top 0.4s ease, box-shadow 0.4s ease, transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1050;
  width: 100%;
  will-change: padding, width, top, transform, border-radius, opacity;
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
}

.glass-navbar > .container-fluid {
  position: relative;
  z-index: 1;
}

.glass-navbar.scrolled {
  padding: 0.5rem 0;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  width: 92%;
  max-width: 1320px;
  border-radius: 20px;
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.1),
    0 8px 16px -8px rgba(15, 16, 53, 0.35),
    0 24px 48px -16px rgba(15, 16, 53, 0.5);
}

/* Backdrop layer: clipped to the bar so the dropdowns can still overflow it */
.nav-bg {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  overflow: hidden;
  pointer-events: none;
}

/* Two soft glows of the brand hue, drifting slowly */
.aurora {
  position: absolute;
  top: 50%;
  width: 520px;
  height: 520px;
  margin-top: -260px;
  border-radius: 50%;
  will-change: transform;
}

.aurora-a {
  left: 10%;
  background: radial-gradient(closest-side, rgba(99, 102, 241, 0.34), transparent);
  animation: aurora-a 26s ease-in-out infinite alternate;
}

.aurora-b {
  right: 0;
  background: radial-gradient(closest-side, rgba(139, 92, 246, 0.3), transparent);
  animation: aurora-b 32s ease-in-out infinite alternate;
}

@keyframes aurora-a {
  from { transform: translate3d(-10%, -6%, 0); }
  to   { transform: translate3d(30%, 8%, 0); }
}

@keyframes aurora-b {
  from { transform: translate3d(8%, 8%, 0); }
  to   { transform: translate3d(-34%, -6%, 0); }
}

/* Soft light that follows the pointer (--mx / --my are set from the script) */
.nav-spotlight {
  position: absolute;
  inset: 0;
  background: radial-gradient(260px circle at var(--mx, 50%) var(--my, 50%), rgba(165, 180, 252, 0.14), transparent 70%);
  opacity: 0;
  transition: opacity 0.4s ease;
}

.glass-navbar:hover .nav-spotlight {
  opacity: 1;
}

/* Hairline of light along the bottom edge */
.nav-glowline {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(129, 140, 248, 0.7) 30%, rgba(167, 139, 250, 0.7) 70%, transparent);
}

/*
  ------------------------------------------------------------------
  SCROLL VISIBILITY (Modern Hide/Show)
  ------------------------------------------------------------------
*/
.navbar-hidden {
  transform: translateY(-110%);
  opacity: 0;
  pointer-events: none;
}

.glass-navbar.scrolled.navbar-hidden {
  transform: translateX(-50%) translateY(-150%) scale(0.95);
  opacity: 0;
}

/* Optimize for ultra-wide screens */
@media (min-width: 1600px) {
  .glass-navbar.scrolled {
    max-width: 1440px;
  }
}

/* Disable floating effect on mobile for better UX */
@media (max-width: 991px) {
  .glass-navbar.scrolled {
    width: 100%;
    top: 0;
    left: 0;
    transform: none;
    border-radius: 0;
    box-shadow: 0 10px 28px -14px rgba(15, 16, 53, 0.55);
  }
}

/*
  ------------------------------------------------------------------
  BRANDING
  ------------------------------------------------------------------
*/
.navbar-brand {
  gap: 0.7rem;
  margin-right: 0;
  padding: 0;
}

.brand-icon {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  border-radius: 12px;
  background: linear-gradient(145deg, #818cf8 0%, #4f46e5 100%);
  color: #fff;
  font-size: 1.15rem;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.1),
    0 8px 18px -6px rgba(99, 102, 241, 0.7);
  transition: transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.3s ease;
}

.brand-icon.sm {
  width: 34px;
  height: 34px;
  font-size: 1rem;
  border-radius: 10px;
}

.navbar-brand:hover .brand-icon {
  transform: translateY(-1px);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.1),
    0 12px 22px -6px rgba(99, 102, 241, 0.85);
}

.brand-text-container {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.brand-text {
  font-family: 'Outfit', sans-serif;
  font-weight: 700;
  font-size: 1.2rem;
  line-height: 1;
  color: #fff;
  letter-spacing: -0.02em;
}

.brand-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: 0.6rem;
  font-weight: 600;
  line-height: 1;
  color: #a5b4fc;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

/*
  ------------------------------------------------------------------
  NAVIGATION LINKS
  ------------------------------------------------------------------
*/
.main-nav {
  position: relative;
  gap: 0.15rem;
}

/* Positioned so the links paint above the sliding pill */
.main-nav > .nav-item {
  position: relative;
}

/* Glass pill that glides to the hovered entry */
.nav-hover-pill {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

.nav-hover-pill.is-visible {
  opacity: 1;
  transition:
    opacity 0.2s ease,
    transform 0.32s cubic-bezier(0.2, 0.8, 0.2, 1),
    width 0.32s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.nav-link {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.8rem !important;
  border: 0;
  border-radius: 10px;
  background: transparent;
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.25rem;
  color: rgba(255, 255, 255, 0.76) !important;
  white-space: nowrap;
  transition: color 0.18s ease, background-color 0.25s ease, box-shadow 0.25s ease;
}

.nav-ico {
  font-size: 0.95rem;
  line-height: 1;
  color: #a5b4fc;
  transition: color 0.18s ease;
}

.nav-caret {
  margin-left: -0.1rem;
  font-size: 0.6rem;
  line-height: 1;
  color: rgba(255, 255, 255, 0.45);
  transition: transform 0.25s ease, color 0.18s ease;
}

.nav-link:hover,
.custom-dropdown:hover > .nav-link {
  color: #fff !important;
  background: transparent;
}

.nav-link:hover .nav-ico,
.custom-dropdown:hover > .nav-link .nav-ico {
  color: #e0e7ff;
}

.custom-dropdown:hover .nav-caret {
  color: rgba(255, 255, 255, 0.85);
  transform: rotate(180deg);
}

.nav-link:focus-visible,
.icon-btn:focus-visible,
.user-profile-link:focus-visible {
  outline: 2px solid rgba(199, 210, 254, 0.9);
  outline-offset: 2px;
}

.panel-item:focus-visible {
  outline: 2px solid rgba(var(--tone), 0.55);
  outline-offset: -2px;
}

.nav-link.router-link-active,
.nav-link.is-active {
  color: #fff !important;
  background: rgba(255, 255, 255, 0.12);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.12);
}

.nav-link.router-link-active .nav-ico,
.nav-link.is-active .nav-ico {
  color: #fff;
}

/* Fine marker under the current section */
.nav-link.router-link-active::after,
.nav-link.is-active::after {
  content: '';
  position: absolute;
  left: 30%;
  right: 30%;
  bottom: 0;
  height: 2px;
  border-radius: 2px;
  background: linear-gradient(90deg, transparent, #a5b4fc, transparent);
}

/*
  ------------------------------------------------------------------
  ICON TILES (dropdown panels, profile menu, mobile drawer)
  ------------------------------------------------------------------
*/
.icon-tile {
  position: relative;
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  border-radius: 12px;
  background: linear-gradient(145deg, rgba(var(--tone), 0.18), rgba(var(--tone), 0.07));
  box-shadow: inset 0 0 0 1px rgba(var(--tone), 0.16);
  color: var(--tone-ink);
  font-size: 1.15rem;
  transition: color 0.2s ease, box-shadow 0.25s ease, transform 0.25s cubic-bezier(0.2, 0.8, 0.2, 1);
}

/* Filled gradient revealed on hover / current page */
.icon-tile::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(145deg, var(--tone-from), var(--tone-to));
  opacity: 0;
  transition: opacity 0.2s ease;
}

.icon-tile i {
  position: relative;
  line-height: 1;
}

.icon-tile.sm {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  font-size: 1rem;
}

.icon-tile.xs {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  font-size: 0.95rem;
}

.panel-item:hover .icon-tile,
.panel-item.router-link-active .icon-tile,
.mobile-nav-link.router-link-active .icon-tile,
.user-dropdown .dropdown-item:hover .icon-tile {
  color: #fff;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.25),
    0 8px 16px -6px rgba(var(--tone), 0.6);
}

.panel-item:hover .icon-tile::before,
.panel-item.router-link-active .icon-tile::before,
.mobile-nav-link.router-link-active .icon-tile::before,
.user-dropdown .dropdown-item:hover .icon-tile::before {
  opacity: 1;
}

.panel-item:hover .icon-tile {
  transform: translateY(-1px);
}

/*
  ------------------------------------------------------------------
  DROPDOWN PANELS (hover / keyboard)
  ------------------------------------------------------------------
*/
.custom-dropdown {
  position: relative;
}

.nav-panel {
  position: absolute;
  top: calc(100% + 14px);
  left: 50%;
  z-index: 1100;
  width: max-content;
  padding: 0.5rem;
  background: #fff;
  border: 1px solid rgba(15, 16, 53, 0.08);
  border-radius: 18px;
  box-shadow:
    0 1px 2px rgba(15, 16, 53, 0.05),
    0 14px 28px -8px rgba(15, 16, 53, 0.16),
    0 36px 72px -18px rgba(49, 46, 129, 0.28);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: translateX(-50%) translateY(8px) scale(0.98);
  transform-origin: top center;
  transition: opacity 0.18s ease, transform 0.24s cubic-bezier(0.2, 0.8, 0.2, 1), visibility 0.18s;
}

/* Keeps the panel open while the pointer crosses the gap under the link */
.nav-panel::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: -18px;
  height: 18px;
}

.custom-dropdown:hover .nav-panel {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transform: translateX(-50%) translateY(0) scale(1);
}

/* Keyboard access: separate rule so browsers without :has() keep the hover rule */
.custom-dropdown:has(:focus-visible) .nav-panel {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transform: translateX(-50%) translateY(0) scale(1);
}

.custom-dropdown.is-dismissed .nav-panel {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}

.panel-label {
  padding: 0.5rem 0.75rem 0.45rem;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}

.panel-grid {
  display: grid;
  grid-template-columns: minmax(270px, auto);
  gap: 2px;
}

.nav-panel.two-cols .panel-grid {
  grid-template-columns: repeat(2, minmax(250px, auto));
}

.panel-item {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.6rem 1rem 0.6rem 0.65rem;
  border-radius: 12px;
  text-decoration: none;
  opacity: 0;
  transform: translateY(6px);
  transition: background-color 0.15s ease, opacity 0.12s ease, transform 0.12s ease;
}

/* Entries ease in one after the other when the panel opens */
.custom-dropdown:hover .panel-item {
  opacity: 1;
  transform: translateY(0);
  transition:
    background-color 0.15s ease,
    opacity 0.28s ease calc(var(--i, 0) * 28ms + 40ms),
    transform 0.34s cubic-bezier(0.2, 0.8, 0.2, 1) calc(var(--i, 0) * 28ms + 40ms);
}

.custom-dropdown:has(:focus-visible) .panel-item {
  opacity: 1;
  transform: translateY(0);
}

.panel-item:hover {
  background: rgba(var(--tone), 0.06);
}

.panel-item.router-link-active {
  background: rgba(var(--tone), 0.08);
}

.item-text {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.item-title {
  display: flex;
  align-items: center;
  gap: 0.1rem;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25rem;
  color: #0f172a;
  white-space: nowrap;
}

.item-desc {
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.05rem;
  color: #64748b;
  white-space: nowrap;
}

.item-arrow {
  font-size: 1.15rem;
  line-height: 1;
  color: var(--tone-ink);
  opacity: 0;
  transform: translateX(-6px);
  transition: opacity 0.18s ease, transform 0.22s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.panel-item:hover .item-arrow {
  opacity: 1;
  transform: translateX(0);
}

/*
  ------------------------------------------------------------------
  ACTIONS (help, notifications, profile)
  ------------------------------------------------------------------
*/
.nav-actions {
  gap: 0.4rem;
}

.icon-btn,
.nav-actions :deep(.notification-bell) {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  min-width: 40px;
  height: 40px;
  padding: 0 0.6rem;
  border: 0;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.07);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.09);
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.82);
  text-decoration: none;
  transition: color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease;
}

.icon-btn i,
.nav-actions :deep(.notification-bell i) {
  font-size: 1.1rem;
  line-height: 1;
}

.icon-btn:hover,
.nav-actions :deep(.notification-bell:hover),
.nav-actions :deep(.notification-bell[aria-expanded="true"]) {
  background: rgba(255, 255, 255, 0.14);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.18);
  color: #fff;
}

.nav-actions :deep(.notification-badge) {
  border-color: #2a2770;
}

.nav-divider {
  width: 1px;
  height: 24px;
  margin: 0 0.3rem;
  background: rgba(255, 255, 255, 0.14);
}

.user-profile-link {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.25rem;
  border: 0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.07);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

.user-profile-link:hover,
.user-profile-link[aria-expanded="true"] {
  background: rgba(255, 255, 255, 0.14);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.22);
}

.avatar-circle {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  border-radius: 50%;
  background: linear-gradient(145deg, #818cf8 0%, #4f46e5 100%);
  color: #fff;
  font-family: 'Outfit', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.16);
}

.avatar-circle.lg {
  width: 42px;
  height: 42px;
  font-size: 0.95rem;
}

.user-meta {
  gap: 2px;
}

.user-name {
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.1;
  color: #fff;
  white-space: nowrap;
}

.user-role {
  font-size: 0.7rem;
  font-weight: 500;
  line-height: 1.1;
  color: #a5b4fc;
  white-space: nowrap;
}

.user-caret {
  margin-right: 0.5rem;
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.55);
  transition: transform 0.25s ease;
}

.user-profile-link[aria-expanded="true"] .user-caret {
  transform: rotate(180deg);
}

/* User Dropdown Menu - Click based */
.user-dropdown {
  min-width: 264px;
  margin-top: 0.85rem !important;
  padding: 0.4rem;
  background: #fff;
  border: 1px solid rgba(15, 16, 53, 0.08);
  border-radius: 16px;
  box-shadow:
    0 1px 2px rgba(15, 16, 53, 0.05),
    0 14px 28px -8px rgba(15, 16, 53, 0.16),
    0 36px 72px -18px rgba(49, 46, 129, 0.28);
}

/* Identity card (profile menu + mobile drawer) */
.user-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.35rem;
  padding: 0.75rem;
  border-radius: 12px;
  background: linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%);
  box-shadow: inset 0 0 0 1px rgba(79, 70, 229, 0.1);
}

.user-card-text {
  min-width: 0;
}

.user-card-name {
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.25rem;
  color: #1e1b4b;
}

.user-card-email {
  font-size: 0.75rem;
  color: #6366f1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-dropdown .dropdown-divider {
  margin: 0.3rem 0.4rem;
  border-color: rgba(15, 23, 42, 0.07);
  opacity: 1;
}

.user-dropdown .dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.4rem 0.5rem;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #1e293b;
  transition: color 0.15s ease, background-color 0.15s ease;
}

.user-dropdown .dropdown-item:hover,
.user-dropdown .dropdown-item:focus {
  background: rgba(var(--tone), 0.06);
  color: #0f172a;
}

.user-dropdown .dropdown-item.is-danger,
.user-dropdown .dropdown-item.is-danger:hover {
  color: #be123c;
}

@media (max-width: 991px) {
  .user-dropdown {
    position: absolute !important;
    right: 0 !important;
    left: auto !important;
    transform: none !important;
    z-index: 1060;
  }
}

/*
  ------------------------------------------------------------------
  MOBILE DRAWER
  ------------------------------------------------------------------
*/
.mobile-toggle-btn {
  padding: 0;
  color: #fff;
}

.hamburger-icon span {
  display: block;
  width: 18px;
  height: 2px;
  margin: 4px 0;
  border-radius: 2px;
  background-color: currentColor;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.hamburger-icon.active span:nth-child(1) { transform: translateY(6px) rotate(45deg); }
.hamburger-icon.active span:nth-child(2) { opacity: 0; transform: translateX(8px); }
.hamburger-icon.active span:nth-child(3) { transform: translateY(-6px) rotate(-45deg); }

/* Mobile Drawer & Overlay */
.mobile-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 16, 53, 0.45);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 1055;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
}

.mobile-overlay.active {
  opacity: 1;
  visibility: visible;
}

.mobile-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 86%;
  max-width: 340px;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  background: #fff;
  z-index: 1060;
  box-shadow: -24px 0 48px -12px rgba(15, 16, 53, 0.35);
  transform: translateX(105%);
  visibility: hidden;
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), visibility 0s linear 0.4s;
}

.mobile-drawer.open {
  transform: translateX(0);
  visibility: visible;
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), visibility 0s;
}

.drawer-header {
  padding: 1rem 1.1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(100deg, #0f1035 0%, #1b1a55 55%, #2e2a7a 100%);
}

.btn-close-drawer {
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 0.85rem;
  transition: background-color 0.18s ease;
}

.btn-close-drawer:hover {
  background: rgba(255, 255, 255, 0.16);
}

.drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 0.85rem 1.25rem;
}

.user-drawer-info {
  margin-bottom: 0.9rem;
}

.drawer-nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.nav-section-label {
  margin: 1.1rem 0 0.35rem 0.6rem;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}

.mobile-nav-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.4rem 0.6rem;
  border-radius: 12px;
  font-size: 0.92rem;
  font-weight: 500;
  color: #1e293b;
  text-decoration: none;
  transition: color 0.18s ease, background-color 0.18s ease;
}

.mobile-nav-link:hover {
  background: rgba(var(--tone), 0.06);
  color: #0f172a;
}

.mobile-nav-link.router-link-active {
  background: rgba(var(--tone), 0.09);
  color: var(--tone-ink);
  font-weight: 600;
}

.drawer-footer {
  padding: 0.85rem;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}

.drawer-logout {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.7rem;
  border: 0;
  border-radius: 12px;
  background: rgba(244, 63, 94, 0.08);
  font-size: 0.9rem;
  font-weight: 600;
  color: #be123c;
  transition: background-color 0.18s ease;
}

.drawer-logout:hover {
  background: rgba(244, 63, 94, 0.14);
}

/* Responsive adjustments */
@media (max-width: 991.98px) {
  .container-fluid { padding-inline: 1rem !important; }
}

/* Tighter links where brand, six entries and the profile share one row */
@media (min-width: 992px) and (max-width: 1199.98px) {
  .nav-link {
    gap: 0.4rem;
    padding: 0.5rem 0.55rem !important;
    font-size: 0.85rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .aurora {
    animation: none !important;
  }

  .glass-navbar,
  .nav-hover-pill,
  .nav-panel,
  .panel-item,
  .icon-tile,
  .icon-tile::before,
  .item-arrow,
  .nav-caret,
  .user-caret,
  .mobile-drawer,
  .mobile-overlay {
    transition: none !important;
  }
}
</style>
